import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { RECOMMENDED_LIMITS, type PlanKey } from "./tokenCostEstimator";

/**
 * Sponsored Premium: si el usuario llega vía Univision o un operador
 * patrocinador (?src=univision, ?partner=…), se le concede plan "pro"
 * temporalmente sin costo. Server-trust no aplica en demo.
 */
export function detectSponsoredPlan(): PlanKey {
  if (typeof window === "undefined") return "free";
  try {
    const sp = new URLSearchParams(window.location.search);
    const src = (sp.get("src") || sp.get("partner") || "").toLowerCase();
    const sponsored = ["univision", "tudn", "espn", "draftkings", "fanduel", "caliente"];
    if (sponsored.includes(src)) {
      void import("@/lib/audit").then((m) =>
        m.trackEvent("ai_sponsored_premium_unlocked", { src }),
      );
      return "pro";
    }
  } catch {
    /* noop */
  }
  return "free";
}

export interface CreditRow {
  session_id: string;
  plan: PlanKey;
  daily_tokens_used: number;
  daily_messages_used: number;
  credits_remaining: number;
  reset_at: string;
}

/** Best-effort fetch+upsert of the current session's credit row. */
export async function ensureCreditRow(plan: PlanKey = "free"): Promise<CreditRow | null> {
  try {
    const session_id = getSessionId();
    const { data } = await supabase
      .from("ai_usage_credits")
      .select("session_id,plan,daily_tokens_used,daily_messages_used,credits_remaining,reset_at")
      .eq("session_id", session_id)
      .maybeSingle();
    if (data) return data as CreditRow;
    const limits = RECOMMENDED_LIMITS[plan];
    const insert = {
      session_id,
      plan,
      daily_tokens_used: 0,
      daily_messages_used: 0,
      credits_remaining: limits.messagesPerDay < 0 ? 9_999 : limits.messagesPerDay,
    };
    const { data: ins } = await supabase
      .from("ai_usage_credits")
      .insert(insert)
      .select("session_id,plan,daily_tokens_used,daily_messages_used,credits_remaining,reset_at")
      .maybeSingle();
    return (ins as CreditRow) ?? null;
  } catch {
    return null;
  }
}

export async function consumeCredits(opts: { tokens: number; plan?: PlanKey }): Promise<{
  allowed: boolean;
  remaining: number;
}> {
  const plan = opts.plan ?? "free";
  const current = await ensureCreditRow(plan);
  if (!current) return { allowed: true, remaining: 1 };
  const limits = RECOMMENDED_LIMITS[plan];
  const unlimited = limits.messagesPerDay < 0;
  const nextMessages = current.daily_messages_used + 1;
  const nextTokens = current.daily_tokens_used + opts.tokens;
  const overMessages = !unlimited && nextMessages > limits.messagesPerDay;
  const overTokens = !unlimited && nextTokens > limits.tokensPerDay;
  if (overMessages || overTokens) {
    void trackEvent("ai_limit_reached", { plan, reason: overTokens ? "tokens" : "messages" });
    return { allowed: false, remaining: 0 };
  }
  const remaining = unlimited ? 9_999 : Math.max(0, limits.messagesPerDay - nextMessages);
  try {
    await supabase
      .from("ai_usage_credits")
      .update({
        daily_tokens_used: nextTokens,
        daily_messages_used: nextMessages,
        credits_remaining: remaining,
      })
      .eq("session_id", current.session_id);
  } catch {
    /* noop */
  }
  void trackEvent("ai_credit_consumed", { plan, tokens: opts.tokens, remaining });
  return { allowed: true, remaining };
}
